import { useAppStore } from './store/useAppStore';
import { AuthForm } from './components/AuthForm';
import { SaveGameSelector } from './components/SaveGameSelector';
import { WalnutList } from './components/WalnutList';
import { ArrowLeft } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';

// Simple button component since shadcn/ui button is not available
const Button = ({ 
  children, 
  onClick, 
  variant = 'default',
  size = 'default',
  className = '',
  ...props 
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  [key: string]: any;
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  const variants = {
    default: 'bg-blue-600 hover:bg-blue-700',
    ghost: '', // Remove default hover style
    outline: 'border border-gray-300 bg-white',
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 px-3',
    lg: 'h-11 px-8',
    icon: 'h-10 w-10',
  };

  // Only apply variant styles if className doesn't override them
  const variantStyles = variant !== 'ghost' ? variants[variant] : '';

  return (
    <button
      className={`${baseStyles} ${variantStyles} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};


function AppHeader() {
  const user = useAppStore(s => s.user);
  const currentView = useAppStore((s) => s.currentView);
  const setCurrentSave = useAppStore((s) => s.setCurrentSave);
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleBackToSaves = () => {
    setCurrentSave(null);
  };

  return (
    <header className="bg-gradient-to-r from-blue-400 to-blue-700 shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {currentView === 'walnutList' && (
              <Button 
                variant="ghost" 
                size="icon"
                className="bg-transparent hover:bg-blue-500"
                onClick={handleBackToSaves}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1
              className="text-xl font-semibold cursor-pointer hover:underline"
              onClick={handleGoHome}
            >
              Stardew Valley
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {!user && (
              <>
                <Link to="/login">
                  <Button variant="outline" size="sm" className='text-gray-700 hover:bg-gray-300'>Zaloguj się</Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" size="sm" className='text-gray-700 hover:bg-gray-300'>Rejestracja</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function MainRoutes() {
  const user = useAppStore(s => s.user);
  const currentView = useAppStore((s) => s.currentView);
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthForm />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <AuthForm />} />
      <Route path="/" element={
        user ? (
          <main className="container mx-auto px-4 py-6">
            {currentView === 'selectSave' ? (
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">Select a Save File</h2>
                <SaveGameSelector />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <WalnutList />
              </div>
            )}
          </main>
        ) : (
          <div className="max-w-xl mx-auto mt-12 text-center text-gray-600 text-lg bg-white rounded-lg shadow p-8">
            <p>Aby korzystać z zapisywania i przeglądania save'ów, musisz być zalogowany.</p>
            <div className="mt-6 flex justify-center gap-4">
              <Link to="/login">
                <Button variant="outline">Zaloguj się</Button>
              </Link>
              <Link to="/register">
                <Button variant="outline">Rejestracja</Button>
              </Link>
            </div>
          </div>
        )
      } />
    </Routes>
  );
}

import { useEffect } from 'react';
function AppContent() {
  const loadData = useAppStore(s => s.loadData);
  const dataLoading = useAppStore(s => s.dataLoading);
  
  useEffect(() => {
    // Only load data once on mount, not on every render
    console.log('App mounted, loading data...');
    loadData();
  }, []); // Empty dependency array - only run once
  
  // Show loading state while data is being loaded
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <MainRoutes />
      {/* Footer */}
      <footer className="bg-white py-4 mt-8">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          <p>Not affiliated with ConcernedApe or Stardew Valley.</p>
          <p className="text-xs mt-1">
            Data sourced from the{' '}
            <a 
              href="https://stardewvalleywiki.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Stardew Valley Wiki
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
